import { type ReactNode, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import {
    ButtonIcon,
    Dialog,
    DialogBody,
    DialogHeader,
    DialogTrigger,
} from "@backstage/ui";
import { RiMenuLine } from "@remixicon/react";

import { type SidebarEntry } from "../types";
import styles from "./MobileSidebar.module.css";
import { SidebarItem } from "./SidebarItem";
import { SidebarOpenStateProvider } from "./sidebarOpenState";

/**
 * Navigation for viewports below BUI's `xs` breakpoint (640px).
 *
 * Replaces `MobileSidebar` from `@backstage/core-components`, but not
 * like-for-like. The original rendered every `SidebarGroup` as a tab in a
 * bottom bar, each opening its own overlay — a structure that only made sense
 * because groups doubled as mobile tabs. Here the bar holds a small fixed set
 * of shortcuts plus a menu button, and the full navigation lives in one
 * drawer, which keeps the desktop grouping intact instead of reinterpreting
 * it.
 *
 * The bar is 56px tall and `AppLayout` reserves matching bottom padding, so it
 * never covers page content.
 *
 * Inside the drawer the rail is treated as expanded, so items render with
 * their labels — there is no hover affordance on touch to reveal them.
 *
 * The drawer's open state is controlled rather than left to `DialogTrigger`,
 * because a modal does not dismiss itself when something inside it navigates.
 * Uncontrolled, tapping a link routed the page underneath and left the drawer
 * covering it. Closing is driven from two places, and both are needed:
 * navigation covers ordinary taps, and the link click covers taps on the page
 * you are already on, where the location never changes.
 */
export const MobileSidebar = (props: {
    quickLinks: SidebarEntry[];
    children: ReactNode;
}) => {
    const { quickLinks, children } = props;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { pathname, search } = useLocation();

    // Catalog entries differ only by query string, so the search has to be
    // part of this or those links would leave the drawer open.
    useEffect(() => {
        setIsMenuOpen(false);
    }, [pathname, search]);

    return (
        <nav
            aria-label="Main"
            data-testid="mobile-sidebar"
            className={styles.bar}
        >
            <SidebarOpenStateProvider isOpen={false}>
                <DialogTrigger isOpen={isMenuOpen} onOpenChange={setIsMenuOpen}>
                    <ButtonIcon
                        variant="tertiary"
                        aria-label="Open navigation menu"
                        icon={<RiMenuLine size={20} aria-hidden />}
                    />
                    <Dialog className={styles.drawer}>
                        <DialogHeader>Menu</DialogHeader>
                        <DialogBody>
                            <SidebarOpenStateProvider isOpen>
                                {/*
                                 * A landmark of its own, not a plain `<div>`.
                                 * BUI's `Dialog` renders through React Aria's
                                 * `ModalOverlay`, which portals to
                                 * `document.body` — so this subtree leaves the
                                 * `<nav>` above and would otherwise sit in no
                                 * landmark at all, putting the entire
                                 * navigation out of reach of landmark
                                 * navigation on mobile. The bottom bar keeps
                                 * the "Main" name; this is named separately
                                 * because both are in the accessibility tree
                                 * while the drawer is open.
                                 */}
                                <nav
                                    aria-label="All pages"
                                    className={styles.drawerNav}
                                    // Capture phase, because BUI's `Link` is
                                    // built on react-aria's `usePress`, which
                                    // stops the click propagating before a
                                    // bubble-phase handler here would see it.
                                    onClickCapture={event => {
                                        // Only links dismiss the drawer.
                                        // Submenu disclosures are buttons and
                                        // must be able to expand in place.
                                        if (
                                            (
                                                event.target as HTMLElement
                                            ).closest("a")
                                        ) {
                                            setIsMenuOpen(false);
                                        }
                                    }}
                                >
                                    {children}
                                </nav>
                            </SidebarOpenStateProvider>
                        </DialogBody>
                    </Dialog>
                </DialogTrigger>
                {quickLinks.map(link => (
                    <SidebarItem
                        key={link.href}
                        href={link.href}
                        text={link.title}
                        icon={link.icon}
                    />
                ))}
            </SidebarOpenStateProvider>
        </nav>
    );
};
